/**
 * Every screen-space sprite `/fly` draws: the effect bursts, the damage-smoke
 * trail, the fire on anything alight — gasbag or aeroplane, the player's own
 * included — and whatever sprite nodes the loaded region's scenery placed.
 *
 * One owner rather than four, because they all want the same three things —
 * the baked sheet, the placed camera, and `three/sprite-screen-size.ts`'s
 * sizing rule — and because pooling by *sheet* is what keeps this to a couple
 * of draw calls. `EXPLO.BMP` and `SMOKE.BMP` are one pool each however many
 * bursts and puffs are live.
 *
 * The pure halves live elsewhere and are tested without a GPU:
 * `effect-sprites.ts` owns the burst/plume lifecycle, `smoke-trail.ts` the
 * puffs, `three/sprite-screen-size.ts` the sizing and LOD arithmetic. This
 * file is the three.js plumbing between them.
 *
 * **What actually fires these.** Explosions come from the encounter's own
 * `destroyed` events (`encounter-views.ts`'s `effectSpawnsOf`) and the gasbag
 * fire from `combat/targets.ts`'s ignition state. `FLAK` joined them with
 * AD-010: the ground-fire pass hands the encounter a per-tick list of bursts,
 * and `effectSpawnsOf` turns each into a `flakBurst` spawn. It is drawn through
 * exactly the machinery this file already had — one more entry in
 * `EFFECT_MODELS`, one more pool — which is what the previous note here
 * predicted it would take.
 */

import * as THREE from "three/webgpu";
import { loadBakedModel, type BakedModelBody, type BakedModelLodBody, type BakedModelSpriteSheet } from "../three/baked-model.ts";
import { ScreenSpritePool } from "../three/screen-sprites.ts";
import {
  apparentSizePx,
  billboardForState,
  detailLadder,
  lodForApparentSize,
  stateCountOf,
} from "../three/sprite-screen-size.ts";
import type { WorldBillboard } from "../three/world-instancer.ts";
import {
  advanceEffects,
  appendPlumeSources,
  burstStateIndex,
  clearsLens,
  createEffectPool,
  spawnBurst,
  spawnPlume,
  MAX_BURSTS,
  type BurstKind,
  type EffectPool,
} from "./effect-sprites.ts";
import type { EffectSpawn } from "./encounter-views.ts";
import {
  advanceSmoke,
  createSmokeState,
  puffOpacity,
  MAX_PUFFS,
  type Puff,
  type SmokeSource,
  type SmokeState,
} from "./smoke-trail.ts";

/**
 * The baked model each burst kind animates. `FLAK-00` is the large of the four
 * baked flak objects (30.8 m bounding radius against `FLAK-01`/`-02`'s 6.2 m)
 * and the only one whose frame index is exactly `4·state + lod` across all
 * sixteen, so it is the one that plays as a clean four-state sequence. The
 * original picks one of the four at random per shell; the port draws them all
 * with this one, which is a rendering simplification and nothing more — the
 * shell's position, timing and effect are the same either way.
 */
const EFFECT_MODELS: Readonly<Partial<Record<BurstKind, string>>> = {
  explosion: "models/EXPLO",
  flakBurst: "models/FLAK-00",
};

const SMOKE_MODEL = "models/SMOKE";

/**
 * Which state of the fireball sequence anything merely *alight* holds.
 *
 * `EXPLO`'s state axis walks two things at once. `EXPLO.BMP` is three six-frame
 * sequences (docs/formats/models-tbl.md §4.5) and they run **hot to cold as well
 * as small to large**: the four states `EXPLO#0` shows at its top LOD are frames
 * 3, 5, 11 and 17 — a 32×12 yellow core, a 72×29 yellow-white bloom, an 80×31
 * orange ball and an 88×32 dark red one. So the end of the sequence is the
 * fireball *dying*, which is right for a wreck playing it through and wrong for
 * something still burning: held every frame it is 88×32 of a 200-line frame, a
 * sixth of the screen's height at every range inside 74 m, centred on an
 * airframe it then covers (`docs/engineering/proof/self-effects-chase-*.png`,
 * captured on a real GPU either side of this constant).
 *
 * A sustained flame is the hot end, and state 0 is that end at every rung of the
 * ladder — the first sequence's small yellow-cored frames. The object's own data
 * therefore picks the size, with no scale factor invented on top of it.
 * `sprite-screen-size.test.ts` pins the frame-index ranges this rests on.
 */
/**
 * A held flame alternates the two hot states of `EXPLO.BMP`'s ladder — the
 * 32×12 yellow core and the 72×29 yellow-white bloom — instead of freezing on
 * one. A single held frame read as "half a fireball" (user, 2026-08-21): the
 * hot core alone is a wide slice, and a flame that never moves reads as a
 * texture glitch, not a fire. ~7 Hz is flame-flicker territory; each fire's
 * phase comes from its position so a formation of burners doesn't blink in
 * unison. Presentation only — burn state and cadence stay the engine's.
 */
const SUSTAINED_FIRE_STATES: readonly number[] = [0, 1];
const FIRE_FLICKER_HZ = 7;

/** One loaded sprite object plus the pool its frames are drawn from. */
interface SpriteSource {
  readonly lods: readonly BakedModelLodBody[];
  readonly details: readonly number[];
  readonly boundingRadius: number;
  readonly stateCount: number;
  readonly pool: ScreenSpritePool;
}

function spriteSourceOf(body: BakedModelBody, capacity: number): SpriteSource | undefined {
  const sheet: BakedModelSpriteSheet | undefined = body.spriteSheet;
  if (!sheet) return undefined;
  const lods = [...body.lods].sort((a, b) => a.index - b.index);
  return {
    lods,
    details: detailLadder(lods),
    boundingRadius: body.boundingRadius,
    stateCount: stateCountOf(lods),
    pool: new ScreenSpritePool(sheet, capacity, { fade: body.spriteSheet?.sheet === "SMOKE.BMP" }),
  };
}

/** The LOD §3's ladder picks for this object at this depth, measured through the very projection the sizing rule inverts. */
function lodAt(source: SpriteSource, depthM: number, fovYRadians: number): BakedModelLodBody | undefined {
  const index = lodForApparentSize(source.details, apparentSizePx(source.boundingRadius, depthM, fovYRadians));
  return source.lods[index];
}

/** What the frame loop hands over each frame besides the camera. */
export interface SpriteFrame {
  /** Everything in the sky hurt enough to trail (`frame-signals.ts`); the wreck plumes are added to it here. */
  readonly smokeSources: readonly SmokeSource[];
  /** Everything alight and still in the sky — aeroplanes (the player's included), balloons and Zeppelins (`frame-signals.ts`'s `fireSourcesFor`). */
  readonly fires: readonly FirePoint[];
}

/** A world point holding a flame this frame. */
export interface FirePoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface SpriteEffects {
  /** Add this to the scene once; every pool draws inside it. */
  readonly group: THREE.Group;
  /** Sets an explosion (and, for an aeroplane, its wreck plume) going at a world point. */
  spawn(spawn: EffectSpawn): void;
  /** The loaded region's placed sprite nodes. Empty for every shipped region — `three/world-instancer.ts` has the census. */
  setScenery(billboards: readonly WorldBillboard[]): void;
  /** `camera` must already be placed for this frame — every quad is billboarded off it. */
  update(dt: number, camera: THREE.Camera, fovYRadians: number, frame: SpriteFrame): void;
  /** Draw calls the sprites add this frame: one per pool with anything in it. */
  drawCallCount(): number;
  dispose(): void;
}

class SpriteEffectsImpl implements SpriteEffects {
  readonly group = new THREE.Group();
  private readonly pool: EffectPool = createEffectPool();
  private readonly bursts = new Map<BurstKind, SpriteSource>();
  private readonly scenery = new Map<string, { readonly pool: ScreenSpritePool; readonly items: WorldBillboard[] }>();
  private readonly smokeState: SmokeState = createSmokeState();
  /** Reused every frame: the caller's sources plus this frame's wreck plumes. */
  private readonly sources: SmokeSource[] = [];
  /** This frame's lens position, for `LENS_CLEARANCE_M`. Written once per `update`, never allocated. */
  private readonly lens = new THREE.Vector3();
  private smoke: SpriteSource | undefined;
  /** Drives the held flames' flicker — see `SUSTAINED_FIRE_STATES`. */
  private fireClockS = 0;
  private disposed = false;

  constructor() {
    void this.load();
  }

  private async load(): Promise<void> {
    const entries = Object.entries(EFFECT_MODELS) as [BurstKind, string][];
    const loaded = await Promise.all(
      [...entries.map(([, path]) => path), SMOKE_MODEL].map((path) => loadBakedModel(`${path}.json`).catch(() => undefined)),
    );
    if (this.disposed) return;
    entries.forEach(([kind], index) => {
      const body = loaded[index];
      const source = body ? spriteSourceOf(body, MAX_BURSTS) : undefined;
      if (!source) return;
      this.bursts.set(kind, source);
      this.group.add(source.pool.mesh);
    });
    const smokeBody = loaded[entries.length];
    this.smoke = smokeBody ? spriteSourceOf(smokeBody, MAX_PUFFS) : undefined;
    if (this.smoke) this.group.add(this.smoke.pool.mesh);
  }

  spawn(spawn: EffectSpawn): void {
    const source = this.bursts.get(spawn.kind);
    spawnBurst(this.pool, spawn.kind, spawn.x, spawn.y, spawn.z, source?.stateCount ?? 0);
    if (spawn.plume) spawnPlume(this.pool, spawn.x, spawn.y, spawn.z);
  }

  setScenery(billboards: readonly WorldBillboard[]): void {
    for (const entry of this.scenery.values()) {
      this.group.remove(entry.pool.mesh);
      entry.pool.dispose();
    }
    this.scenery.clear();
    for (const placed of billboards) {
      const existing = this.scenery.get(placed.sheet.path);
      if (existing) {
        existing.items.push(placed);
        continue;
      }
      this.scenery.set(placed.sheet.path, {
        pool: new ScreenSpritePool(placed.sheet, billboards.length),
        items: [placed],
      });
    }
    for (const entry of this.scenery.values()) this.group.add(entry.pool.mesh);
  }

  update(dt: number, camera: THREE.Camera, fovYRadians: number, frame: SpriteFrame): void {
    if (this.disposed) return;
    this.fireClockS += dt;
    advanceEffects(this.pool, dt);
    this.sources.length = 0;
    for (const source of frame.smokeSources) this.sources.push(source);
    appendPlumeSources(this.pool, this.sources);
    advanceSmoke(this.smokeState, this.sources, dt);
    // Taken from the camera itself rather than out of a pool's `begin`, so the
    // three `write*` passes below share one lens whichever pool each draws
    // through — and so the clearance test does not depend on pool ordering.
    camera.getWorldPosition(this.lens);
    for (const source of this.bursts.values()) source.pool.begin(camera, fovYRadians);
    this.smoke?.pool.begin(camera, fovYRadians);
    for (const entry of this.scenery.values()) entry.pool.begin(camera, fovYRadians);

    this.writeBursts(fovYRadians);
    this.writeFires(frame.fires, fovYRadians);
    this.writePuffs(this.smokeState.puffs, fovYRadians);
    for (const entry of this.scenery.values()) {
      for (const placed of entry.items) entry.pool.push(placed.x, placed.y, placed.z, placed.billboard);
    }

    for (const source of this.bursts.values()) source.pool.end();
    this.smoke?.pool.end();
    for (const entry of this.scenery.values()) entry.pool.end();
  }

  /** `effect-sprites.ts`'s `clearsLens` against this frame's lens — see `LENS_CLEARANCE_M` for why every pass asks. */
  private clearsLens(x: number, y: number, z: number): boolean {
    return clearsLens(x, y, z, this.lens.x, this.lens.y, this.lens.z);
  }

  private writeBursts(fovYRadians: number): void {
    for (const burst of this.pool.bursts) {
      const state = burstStateIndex(burst);
      if (state < 0) continue;
      const source = this.bursts.get(burst.kind);
      if (!source) continue;
      if (!this.clearsLens(burst.x, burst.y, burst.z)) continue;
      const lod = lodAt(source, source.pool.depthOf(burst.x, burst.y, burst.z), fovYRadians);
      const billboard = billboardForState(lod, state);
      if (billboard) source.pool.push(burst.x, burst.y, burst.z, billboard);
    }
  }

  /**
   * Everything alight, held rather than played: `combat/targets.ts` keeps a
   * gasbag burning for its whole `burnSeconds` and `combat/combat-damage.ts`
   * keeps an aeroplane burning until its wings go, so a flame is a *state* the
   * renderer reads every frame — not a one-shot like the wreck explosion.
   *
   * Aeroplanes joined the gasbags here rather than getting a system of their
   * own: the visual language is already settled (`SUSTAINED_FIRE_STATE`, the hot
   * end of the fireball sequence), it costs no extra draw call because it shares
   * the `EXPLO.BMP` pool the bursts use, and it means the player's own aeroplane
   * and an opponent's are drawn by the same code. The flame is a world-anchored
   * billboard at the aeroplane's origin, so it reads in the chase and fly-by
   * cameras; from inside the player's own cockpit the lens is *inside* that
   * airframe and `clearsLens` drops it, which is the call `scene-camera.ts`
   * already makes for the mesh the flame belongs to.
   */
  private writeFires(fires: readonly FirePoint[], fovYRadians: number): void {
    const source = this.bursts.get("explosion");
    if (!source) return;
    for (const fire of fires) {
      if (!this.clearsLens(fire.x, fire.y, fire.z)) continue;
      const lod = lodAt(source, source.pool.depthOf(fire.x, fire.y, fire.z), fovYRadians);
      // Position-hashed phase, so two burning aeroplanes flicker out of step.
      const phase = Math.abs(fire.x * 31 + fire.z * 17) % 1;
      const step = Math.floor(this.fireClockS * FIRE_FLICKER_HZ + phase) % SUSTAINED_FIRE_STATES.length;
      const billboard = billboardForState(lod, SUSTAINED_FIRE_STATES[step] ?? 0);
      if (billboard) source.pool.push(fire.x, fire.y, fire.z, billboard);
    }
  }

  private writePuffs(puffs: readonly Puff[], fovYRadians: number): void {
    const source = this.smoke;
    if (!source) return;
    for (const puff of puffs) {
      const opacity = puffOpacity(puff);
      if (opacity <= 0) continue;
      if (!this.clearsLens(puff.x, puff.y, puff.z)) continue;
      const lod = lodAt(source, source.pool.depthOf(puff.x, puff.y, puff.z), fovYRadians);
      // `SMOKE.TBL` has one state per LOD: its ladder is distance, not animation.
      const billboard = billboardForState(lod, 0);
      if (billboard) source.pool.push(puff.x, puff.y, puff.z, billboard, opacity);
    }
  }

  drawCallCount(): number {
    let calls = 0;
    for (const source of this.bursts.values()) calls += source.pool.liveCount > 0 ? 1 : 0;
    if (this.smoke && this.smoke.pool.liveCount > 0) calls += 1;
    for (const entry of this.scenery.values()) calls += entry.pool.liveCount > 0 ? 1 : 0;
    return calls;
  }

  dispose(): void {
    this.disposed = true;
    for (const source of this.bursts.values()) source.pool.dispose();
    this.smoke?.pool.dispose();
    for (const entry of this.scenery.values()) entry.pool.dispose();
    this.bursts.clear();
    this.scenery.clear();
  }
}

export function createSpriteEffects(): SpriteEffects {
  return new SpriteEffectsImpl();
}
